import { InvalidateQueryFilters, QueryClient, useInfiniteQuery, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useCurrentUser, useWorkspaceId } from "../bridges/session";
import { useAccountLocked } from "../bridges/locked";
import { api } from "../data/client";
import { scopedKey } from "../data/keys";
import { useRealtimeInvalidation } from "../data/realtime";
import type { AddressInput, ContactDetail, ContactPayload, DuplicateMatch, PersonInput } from "../contact/api";
import type { AddressProblem, ReportChanges, RequestKind } from "../visits/api";
import { refreshContact } from "../visits/api";

/* ---------- types (wire contract: plan 2D "Endpoint summary (as built)") ---------- */

export type ApprovalKind = RequestKind | "new_contact";
/** `r<id>` = a change request; `c<household id>` = a new contact waiting for approval. */
export type ApprovalRef = `r${number}` | `c${number}`;

/** One compact "To approve" row: who · what · contact · date. */
export type ApprovalRow = {
  ref: ApprovalRef;
  kind: ApprovalKind;
  origin: "report" | "edit" | "old_form";
  /** details: changed fields; new_people / people_left: people. Absent for the other kinds. */
  count?: number;
  household: { id: number; name: string };
  /** null: nobody is named (a new contact today's app added: "Added by —"). `role_label_key` → shell/roleLabel. */
  requested_by: { id: number; name: string; role_label_key: string | null } | null;
  /** ISO 8601. */
  created_at: string;
};

export type ApprovalsPage = { data: ApprovalRow[]; meta: { page: number; last_page: number; total: number } };

export type ApprovalCan = { approve: boolean; reject: boolean; read: boolean };

/**
 * Every change request's review: the report's text and date, `null` for a request without a report (from Edit),
 * ABSENT when the approver has no read_history on the contact. `stops_to_remove` (X3) only with contact details
 * and the routes right.
 */
type RequestReview = {
  note?: string | null;
  visit?: { id: number; date: string | null } | null;
  can: ApprovalCan;
  stops_to_remove?: number;
};

export type DetailsChangeLine = { target: string; target_label: string | null; field: string; old: unknown; new: unknown };
/** A new person as sent; contact details only with see_contact_info. `incomplete` marks blank required names. */
export type NewPersonReview = Omit<PersonInput, "id" | "key"> & {
  incomplete: { first_name: boolean; last_name: boolean; gender: boolean };
};
export type LeavingPerson =
  | { id: number; name: string; phones?: string[]; email?: string | null }
  | { id: number; name: string | null; gone: true };

export type ApprovalDetail =
  | (ApprovalRow & RequestReview & { kind: "details"; changes: DetailsChangeLine[]; unchanged_people: string[] })
  | (ApprovalRow & RequestReview & { kind: "new_people"; people: NewPersonReview[] })
  | (ApprovalRow & RequestReview & { kind: "people_left"; leaving: LeavingPerson[]; staying: { id: number; name: string; is_main: boolean }[] })
  | (ApprovalRow & RequestReview & { kind: "address_problem"; problem: AddressProblem | null; address_line?: string | null })
  | (ApprovalRow & RequestReview & {
      kind: "left_town";
      who_count: number;
      schedule_key: string;
      address_line?: string | null;
      open_stops?: { route_name: string }[];
    })
  | (ApprovalRow & RequestReview & {
      kind: "move";
      type: "home" | "work";
      work_person_id: number | null;
      set_primary: boolean;
      people: { id: number; name: string }[];
      draft_address?: AddressInput | null;
      old_address_line?: string | null;
      open_stops?: { count: number; routes: string[] };
    })
  | (ApprovalRow & {
      kind: "new_contact";
      /** 2C's contact detail (with `version`, for "Edit, then approve"). */
      contact: ContactDetail;
      added_by?: { id: number; name: string; role_label_key: string | null };
      duplicates: DuplicateMatch[];
      can_give_access: boolean;
      can: ApprovalCan;
    });

/**
 * Approve. A change request (`r…`): `edits` in the report's shape for its kind ("Edit, then approve"),
 * `routeStops` for a move (`move` default, or `mark`), `removedStops` (X3, `remove` default), `needsAttention`
 * for an address problem. A new contact (`c…`): `edits` = 2C's edit form with `version`, `giveAccess`, `message`.
 */
export type ApproveInput =
  | {
      ref: `r${number}`;
      householdId: number;
      edits?: NonNullable<ReportChanges["details"]> | NonNullable<ReportChanges["new_people"]> | NonNullable<ReportChanges["left"]>;
      routeStops?: "move" | "mark";
      removedStops?: "remove" | "leave";
      needsAttention?: boolean;
    }
  | { ref: `c${number}`; householdId: number; edits?: ContactPayload; giveAccess?: boolean; message?: string };

export type AnswerTarget = { ref: ApprovalRef; householdId: number };
/** Mark as read exists for change requests only (a new contact has none: `c…` answers 404). */
export type ReadTarget = { ref: `r${number}`; householdId: number };

/** `to_approve` only for approvers. */
export type InboxCounts = { to_approve?: number; updates: number };

export type InboxAction =
  | "report"
  | "plan_more_visits"
  | "change_schedule"
  | "open_contact"
  | "open_history"
  | "open_import"
  | "open_route"
  | "open_routes"
  | "open_volunteering"
  | "open_page"
  | "open_duplicate";

/** One Updates item. Keys without a value are absent. */
export type InboxItem = {
  /** `n<notification uuid>` | `r<id>` (an answer with a reason). */
  id: string;
  /** From config/next_inbox.php: reminder, not_interested, tried_three_times, …, rejected, new_contact_answer. */
  type: string;
  /** On `rejected`: the request's kind. */
  kind?: ApprovalKind;
  /** On `new_contact_answer`: approved | rejected. On `auto_route_finished` (5B B9): ready | nobody | failed. */
  status?: "approved" | "rejected" | "ready" | "nobody" | "failed";
  /** On `auto_route_finished`: how many routes were made. */
  count?: number;
  /** On `auto_route_finished` (failed) and `route_optimized`: why, as a code. */
  reason?: string;
  /** On `route_optimized`. */
  success?: boolean;
  /** Bell details (owner 2026-11-02): the numbers the message kept, per type (InboxDetails on the server). Absent when none were kept. */
  details?: InboxDetails;
  /** On `import_aborted` from the new import: why it stopped (IMPORT_FAILED, CONTACT_LIMIT_REACHED, IMPORT_NOT_ALLOWED). */
  code?: string;
  household?: { id: number; name: string };
  /** On `duplicate_scan_finished` (2F): the marks the check wrote; `failed` only when it stopped before the end. */
  found?: number;
  failed?: true;
  /** On `possible_duplicate`: the contact the new one may duplicate, only when the user reads it. */
  duplicate?: { id: number; name: string };
  route?: { id: number; name: string };
  created_at: string;
  title: { start: string; link_text?: string; end?: string };
  text?: string;
  from?: { id: number; name: string };
  for_me: boolean;
  visit_id?: number;
  read: boolean;
  /** Only what this user may do on this item. */
  actions: InboxAction[];
  path?: string;
};

/** The numbers a message kept (server InboxDetails), by type. Every key is optional: older messages kept less (B5). */
export type InboxDetails = {
  /** import_completed */
  added?: number; people?: number; merged?: number;
  /** import_completed (rows) and routes_batch_created (routes) */
  skipped?: number;
  /** import_aborted over the plan (B6) */
  in_file?: number; now?: number; limit?: number;
  /** auto_route_finished (new runs only) */
  stops?: number; left_out?: number; left_out_by?: Partial<Record<string, number>>;
  /** route_division_completed (today's Auto Route) */
  routes?: number; left_out_no_pin?: number; left_out_size?: number;
  /** route_division_completed and routes_batch_created */
  optimized?: number; not_optimized?: number;
  /** routes_batch_created */
  created?: number; failed?: number; skipped_routes?: { name: string | null; reason: string }[];
  /** addresses_need_attention */
  count?: number;
  /** imported_address_not_found */
  address?: string; approximate?: boolean;
};

export type UpdatesPage = { data: InboxItem[]; meta: { page: number; last_page: number; total: number } };

/* ---------- keys ---------- */

const BASE = "/api/auth/next";

const approvalsKey = (ws: number | null, ...parts: unknown[]) => scopedKey(ws, "approvals", ...parts);
const inboxKey = (ws: number | null, ...parts: unknown[]) => scopedKey(ws, "inbox", ...parts);

/* ---------- queries ---------- */

/** Approvers only (others get a bare 403): pass `can.review_changes` (or `to_approve` being present) as `enabled`. */
export const useApprovals = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: approvalsKey(ws, "list"),
    queryFn: ({ pageParam }) => api.get<ApprovalsPage>(`${BASE}/approvals`, { page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.page < last.meta.last_page ? last.meta.page + 1 : undefined),
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/** The still-waiting items this approver marked Handled: they keep Review (Approve / Reject) and Move back. */
export const useApprovalHandled = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: approvalsKey(ws, "handled"),
    queryFn: ({ pageParam }) => api.get<ApprovalsPage>(`${BASE}/approvals/handled`, { page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.page < last.meta.last_page ? last.meta.page + 1 : undefined),
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/** Handled on a Waiting item only HIDES it for this person; it approves and rejects nothing. */
export const useMarkApprovalHandled = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ref: ApprovalRef) => { await api.post(`${BASE}/approvals/${ref}/handled`); },
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: approvalsKey(ws) }), client.invalidateQueries({ queryKey: inboxKey(ws) })]),
  });
};
export const useApprovalMoveBack = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (ref: ApprovalRef) => { await api.del(`${BASE}/approvals/${ref}/handled`); },
    onSuccess: () => Promise.all([client.invalidateQueries({ queryKey: approvalsKey(ws) }), client.invalidateQueries({ queryKey: inboxKey(ws) })]),
  });
};

export const useApproval = (ref: ApprovalRef, opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useQuery({
    queryKey: approvalsKey(ws, ref),
    queryFn: async () => (await api.get<{ data: ApprovalDetail }>(`${BASE}/approvals/${ref}`)).data,
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

export const useInboxCounts = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  const locked = useAccountLocked(); // a locked account answers 402 every time: nothing is asked
  return useQuery({
    queryKey: inboxKey(ws, "counts"),
    queryFn: async () => (await api.get<{ data: InboxCounts }>(`${BASE}/inbox/counts`)).data,
    enabled: ws !== null && !locked && (opts.enabled ?? true),
  });
};

/** 25 per page, newest first. */
export const useUpdates = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: inboxKey(ws, "updates"),
    queryFn: ({ pageParam }) => api.get<UpdatesPage>(`${BASE}/inbox/updates`, { page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.page < last.meta.last_page ? last.meta.page + 1 : undefined),
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

type InboxEvent = { workspace_id?: unknown; household_id?: unknown } | null;
/** The account a `NextInboxChanged` payload names (`workspace_id`), else the current one. */
const eventWs = (payload: unknown, current: number | null): number | null => {
  const named = (payload as InboxEvent)?.workspace_id;
  return typeof named === "number" ? named : current;
};

/** The bell's keys of the account a `NextInboxChanged` payload names. */
const inboxKeysFor = (current: number | null) => (payload: unknown): unknown[][] => {
  const ws = eventWs(payload, current);
  return [approvalsKey(ws), inboxKey(ws)];
};

/**
 * The waiting marks on screen (final review m8): the contact the payload names (`household_id`, if the server sends
 * one), else every contact shown right now — their details only (["ws", ws, "contact", id]), never a cached one.
 */
const contactFiltersFor = (current: number | null) => (payload: unknown): InvalidateQueryFilters[] => {
  const ws = eventWs(payload, current);
  const named = (payload as InboxEvent)?.household_id;
  if (typeof named === "number") return [{ queryKey: scopedKey(ws, "contact", named), exact: true }];
  return [{ queryKey: scopedKey(ws, "contact"), predicate: (q) => q.queryKey.length === 4 && q.getObserversCount() > 0 }];
};

/**
 * Another approver answered, a request arrived, a contact was deleted…: refresh the bell of the account the event
 * names (on the user's own channel, which carries every account's events), and the waiting marks of the contacts on
 * screen. A background account's bell refreshes too; the current account's only for its own events. Mount it ONCE, in the shell (F9), never per panel or page.
 */
export const useInboxLive = () => {
  const ws = useWorkspaceId();
  const userId = useCurrentUser()?.id;
  useRealtimeInvalidation(userId ? `admin.${userId}` : null, "NextInboxChanged", [approvalsKey(ws), inboxKey(ws)], {
    keysFor: inboxKeysFor(ws),
    filtersFor: contactFiltersFor(ws),
  });
};

/* ---------- mutations ---------- */

/**
 * After an answer: the bell and the contact (an approval or a rejection changes or deletes it; even a read takes its
 * "waiting" marks away). The answered review itself is only marked stale, never read again while it is still open:
 * it is no longer pending, so it would answer 404 ("isn't waiting any more") on the way out.
 */
export const refreshAfterAnswer = (client: QueryClient, ws: number | null, v: AnswerTarget) =>
  Promise.all([
    client.invalidateQueries({ queryKey: approvalsKey(ws, v.ref), exact: true, refetchType: "none" }),
    client.invalidateQueries({ queryKey: approvalsKey(ws), predicate: (q) => q.queryKey[3] !== v.ref }),
    client.invalidateQueries({ queryKey: inboxKey(ws) }),
    refreshContact(client, ws, v.householdId),
  ]);

const useAnswer = <V extends AnswerTarget>(run: (v: V) => Promise<{ status: string }>) => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: run,
    // Not awaited: the answer is done as soon as the server says so (the screen closes then), the lists follow.
    onSuccess: (_r, v) => { void refreshAfterAnswer(client, ws, v); },
  });
};

export const useApprove = () =>
  useAnswer(async (v: ApproveInput) => {
    const body: Record<string, unknown> = {};
    if (v.edits !== undefined) body.edits = v.edits;
    if ("routeStops" in v && v.routeStops !== undefined) body.route_stops = v.routeStops;
    if ("removedStops" in v && v.removedStops !== undefined) body.removed_stops = v.removedStops;
    if ("needsAttention" in v && v.needsAttention !== undefined) body.needs_attention = v.needsAttention;
    if ("giveAccess" in v && v.giveAccess !== undefined) body.give_access = v.giveAccess;
    if ("message" in v && v.message !== undefined) body.message = v.message;
    return (await api.post<{ data: { status: "approved" } }>(`${BASE}/approvals/${v.ref}/approve`, body)).data;
  });

/** An empty reason means none: the reporter is not told. */
export const useReject = () =>
  useAnswer(async (v: AnswerTarget & { reason?: string }) => {
    const reason = v.reason?.trim();
    return (await api.post<{ data: { status: "rejected" } }>(`${BASE}/approvals/${v.ref}/reject`, reason ? { reason } : {})).data;
  });

/** Closes the request without changing anything or telling anyone; the contact's "waiting" marks go with it. */
export const useMarkRead = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (v: ReadTarget) => (await api.post<{ data: { status: "read" } }>(`${BASE}/approvals/${v.ref}/read`)).data,
    onSuccess: (_r, v) => { void refreshAfterAnswer(client, ws, v); },
  });
};

/** `id` = an Updates item's id (`n<uuid>` | `r<id>`). Twice is fine. */
export const useUpdateDone = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ data: { done: true } }>(`${BASE}/inbox/updates/${id}/done`)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: inboxKey(ws) }),
  });
};

/** The read-only Handled list (kept 90 days), newest first. */
export const useHandled = (opts: { enabled?: boolean } = {}) => {
  const ws = useWorkspaceId();
  return useInfiniteQuery({
    queryKey: inboxKey(ws, "handled"),
    queryFn: ({ pageParam }) => api.get<UpdatesPage>(`${BASE}/inbox/handled`, { page: pageParam }),
    initialPageParam: 1,
    getNextPageParam: (last) => (last.meta.page < last.meta.last_page ? last.meta.page + 1 : undefined),
    enabled: ws !== null && (opts.enabled ?? true),
  });
};

/** "Move back": a handled item returns to Updates. */
export const useMoveBack = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => (await api.post<{ data: { moved_back: true } }>(`${BASE}/inbox/updates/${id}/move-back`)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: inboxKey(ws) }),
  });
};

/** The person's notice choices: only what they can receive; e-mail off until turned on. */
export type NoticeChoices = {
  groups: Array<{ key: string; bell: boolean; email: boolean }>;
  always_on: Array<"waiting_approvals" | "billing">;
  daily_digest: { email: boolean } | null;
  /** News from ConnectYid (staff console announcements of kind "news"): everyone has it, on unless turned off here or by the e-mail's link. */
  news?: { email: boolean };
};
export const useNoticeChoices = () => {
  const ws = useWorkspaceId();
  const locked = useAccountLocked(); // a locked account answers 402: nothing is asked
  return useQuery({
    queryKey: inboxKey(ws, "choices"),
    queryFn: async () => (await api.get<{ data: NoticeChoices }>(`${BASE}/me/notice-choices`)).data,
    enabled: ws !== null && !locked,
    staleTime: 15_000,
  });
};
export const useSaveNoticeChoices = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (v: { groups?: Record<string, { bell?: boolean; email?: boolean }>; daily_digest?: boolean; news?: boolean }) =>
      (await api.put<{ data: NoticeChoices }>(`${BASE}/me/notice-choices`, v)).data,
    onSuccess: (data) => client.setQueryData(inboxKey(ws, "choices"), data),
  });
};

/** "Mark all as handled": notifications stay (as read), answers leave the feed. `undo` is the stamp the 10-second Undo hands back. */
export const useUpdatesDoneAll = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async () => (await api.post<{ data: { marked: number; undo?: string } }>(`${BASE}/inbox/updates/done-all`)).data,
    onSuccess: () => client.invalidateQueries({ queryKey: inboxKey(ws) }),
  });
};
/** Undo of Mark all (N2.3): puts back exactly what that press marked. */
export const useUndoDoneAll = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (undo: string) => (await api.post<{ data: { restored: number } }>(`${BASE}/inbox/updates/undo-done-all`, { undo })).data,
    onSuccess: () => client.invalidateQueries({ queryKey: inboxKey(ws) }),
  });
};

/** "Plan 3 more visits" (tried three times): plans visits and answers the contact's open alerts. */
export const usePlanMoreVisits = () => {
  const ws = useWorkspaceId();
  const client = useQueryClient();
  return useMutation({
    mutationFn: async (householdId: number) =>
      (await api.post<{ data: { planned: number } }>(`${BASE}/households/${householdId}/plan-more-visits`)).data,
    onSuccess: (_r, householdId) =>
      Promise.all([client.invalidateQueries({ queryKey: inboxKey(ws) }), refreshContact(client, ws, householdId)]),
  });
};
